import React, { useState, useEffect } from "react";
import { Grid } from "@material-ui/core";
import AmortizationTypes from "constants/amortizationTypes";

import {
  fieldTypes,
  floatingIndexList,
  fixedIndexList
} from "../../../constants";
import {
  amortTypesIdsShort,
  interestRateTypeIds,
  daycountIdsList
} from "../../../components/Calculators/Constants/DropDownLists";
import { floatingIndices } from "../../../components/Admin/Cashflows/configItemValues";
import InterestRateType from "../../../components/Calculators/Constants/InterestRateType";
import {
  TextInput,
  AutoCompleteInput,
  PercentInput,
  IntegerInput,
  MoneyInput,
  DatePicker
} from "../../../components/Global/FormikMaterial";
import { LenderLoanTypeList } from "../../../constants/loanTypes";

const SectionedFieldBody = ({ sections }) => (
  <>
    {sections?.map(({ sectionTitle, fields }, idx) => (
      <Grid container item xs={12} key={idx}>
        <Grid container item xs={12}>
          <div className="loan-proceeds-assumption-form-section-title">{sectionTitle}</div>
        </Grid>
        {renderFields(fields)}
      </Grid>
    ))}
  </>
);

const renderFields = fieldsArray => (
    fieldsArray?.map((field, idx) => {
      if (field.hidden) {
        return <Grid item lg={3} md={6} xs={12} key={idx} style={{ display: "none" }} />;
      }

      switch (field.fieldType) {
        case fieldTypes.PERCENT_INPUT:
          return (displayField(<PercentInput {...field} key={idx} />));
        case fieldTypes.AUTOCOMPLETE_INPUT:
          return (displayField(<AutoCompleteInput {...field} key={idx} enableDarkerBackground enableModifiedTinyHelperMargin />));
        case fieldTypes.INTEGER_INPUT:
          return (displayField(<IntegerInput {...field} key={idx} />));
        case fieldTypes.MONEY_INPUT:
          return (displayField(<MoneyInput {...field} key={idx} />));
        case fieldTypes.DATE_PICKER:
          return (displayField(<DatePicker {...field} key={idx} />));
        default:
          return (displayField(<TextInput {...field} key={idx} />));
      }
    })
);

const displayField = props => <Grid item lg={3} md={6} xs={12} key={props.key} style={{ marginTop: "20px" }}>{props}</Grid>;

export const SupervisedAssumptionFormContent = ({ isFavorite, favoriteAssumption, loanTermYears, capRate: propsCapRate, noi: propsNoi, setFieldValue, values: formikValues, preferredConstraintsList, savedAssumptions }) => {
  const {
    lpsRateType,
    lpsAmortizationType
  } = formikValues;

  const {
    amortTermMonths,
    amortRateIfFloating,
    amortTermMonthsForCashflows,
    amortRateIfFloatingForCashflows,
    closingCostsPercentage,
    uwRate,
    ioPeriods
  } = savedAssumptions?.spread?.loanTypeSetting || {};

  const preferredConstraintId = savedAssumptions?.spread?.loanTypeSetting?.preferredConstraint?.value;
  const rateIndexId = savedAssumptions?.spread?.loanTypeSetting?.rateIndex?.value;
  const daycountId = savedAssumptions?.spread?.loanTypeSetting?.dayCount?.value;
  const loanTypeId = savedAssumptions?.spread?.loanTypeSetting?.loanType?.value;
  const rateTypeId = savedAssumptions?.spread?.loanTypeSetting?.rateType?.value;
  const amortTypeId = savedAssumptions?.spread?.loanTypeSetting?.amortType?.value;

  const {
    spread
  } = savedAssumptions?.spread || {};

  const {
    ltvMax,
    dscrMin,
    debtYieldMin
  } = savedAssumptions?.spread?.constraint || {};

  const [rateIndexOptions, setRateIndexOptions] = useState([]);
  const [amortRateFieldHidden, setAmortRateFieldHidden] = useState(true);
  const [ioPeriodFieldHidden, setIoPeriodFieldHidden] = useState(true);

  useEffect(() => {
    setFieldValue("lpsRateIndex", "");
    setFieldValue("lpsAmortizationRateForCashflows", "");
    setFieldValue("lpsAmortizationRate", lpsRateType === InterestRateType.FIXED_RATE_ID ? "" : amortRateIfFloating);
    setFieldValue("lpsAmortizationRateForCashflows", lpsRateType === InterestRateType.FIXED_RATE_ID ? "" : amortRateIfFloatingForCashflows);
    const floatingIndexOptions = floatingIndexList.filter(option => option.value !== floatingIndices.Thirty_Day_Avg_SOFR);
    setRateIndexOptions(lpsRateType === InterestRateType.FIXED_RATE_ID ? fixedIndexList : floatingIndexOptions);
    setFieldValue("lpsRateIndex", rateIndexId ?? "");
    setAmortRateFieldHidden(lpsRateType === InterestRateType.FIXED_RATE_ID);
  }, [lpsRateType]);

  useEffect(() => {
    lpsAmortizationType === AmortizationTypes.INTEREST_ONLY ? setIoPeriodFieldHidden(true) : setIoPeriodFieldHidden(false);
  }, [lpsAmortizationType]);

  useEffect(() => {
    if (!isFavorite) {
      setFieldValue("lpsRateIndex", rateIndexId ?? "");
      setFieldValue("lpsAmortizationTerm", amortTermMonths ?? "");
      setFieldValue("lpsAmortizationRate", amortRateIfFloating ?? "");
      setFieldValue("lpsAmortizationType", amortTypeId ?? "");
      setFieldValue("lpsClosingCosts", closingCostsPercentage * 100 ?? "");
      setFieldValue("lpsRateType", rateTypeId ?? "");
      setFieldValue("lpsAmortizationTermForCashflows", amortTermMonthsForCashflows ?? "");
      setFieldValue("lpsAmortizationRateForCashflows", amortRateIfFloatingForCashflows ?? "");
      setFieldValue("lpsIoPeriods", ioPeriods ?? "");
      setFieldValue("lpsDaycount", daycountId ?? "");
      setFieldValue("lpsConstraintPreferenceId", preferredConstraintId ?? "");
      setFieldValue("loanTypeId", loanTypeId ?? "");
      setFieldValue("ltvMax", ltvMax * 100 ?? "");
      setFieldValue("dscrMin", dscrMin ?? "");
      setFieldValue("debtYieldMin", debtYieldMin * 100 ?? "");
      setFieldValue("spread", spread * 100 ?? "");
      setFieldValue("noi", propsNoi ?? "");
      setFieldValue("futureCapRate", propsCapRate * 100 ?? "");
      setFieldValue("startDate", new Date() ?? "");
      setFieldValue("loanTerm", loanTermYears * 12 ?? "");
      setFieldValue("uwRate", uwRate * 100);
    } else {
      const {
        UwRateRaw,
        amortization,
        amortizationRate,
        amortizationTerm,
        amortizationTypeId,
        closingCosts,
        dayCountId,
        indexId,
        interestOnlyPeriods,
        loanSpread,
        loanType,
        maxLTV,
        minDSCR,
        minDebtYield,
        preferredConstraintId,
        rateTypeId,
        term,
        capRate,
        noi
      } = favoriteAssumption;

      setFieldValue("lpsRateIndex", indexId ?? "");
      setFieldValue("lpsAmortizationTerm", amortization ?? "");
      setFieldValue("lpsAmortizationRate", amortRateIfFloating ?? "");
      setFieldValue("lpsAmortizationType", amortizationTypeId ?? "");
      setFieldValue("lpsClosingCosts", closingCosts ?? "");
      setFieldValue("lpsRateType", rateTypeId ?? "");
      setFieldValue("lpsAmortizationTermForCashflows", amortizationTerm ?? "");
      setFieldValue("lpsAmortizationRateForCashflows", amortizationRate ?? "");
      setFieldValue("lpsIoPeriods", interestOnlyPeriods ?? "");
      setFieldValue("lpsDaycount", dayCountId ?? "");
      setFieldValue("lpsConstraintPreferenceId", preferredConstraintId ?? "");
      setFieldValue("loanTypeId", loanType ?? "");
      setFieldValue("ltvMax", maxLTV ?? "");
      setFieldValue("dscrMin", minDSCR ?? "");
      setFieldValue("debtYieldMin", minDebtYield ?? "");
      setFieldValue("spread", loanSpread ?? "");
      setFieldValue("noi", noi ? noi.toString() : "");
      setFieldValue("futureCapRate", capRate ? (capRate * 100).toString() : "0");
      setFieldValue("startDate", new Date() ?? "");
      setFieldValue("loanTerm", term ?? "");
      setFieldValue("uwRate", UwRateRaw * 100 ?? "");
    }
  }, [savedAssumptions, propsNoi, propsCapRate, loanTermYears, isFavorite, favoriteAssumption]);

  const underwritingAssumptionsSection = [
    {
      sectionTitle: "Underwriting Assumptions",
      fields: [
        {
          label: "Loan Type",
          fieldName: "loanTypeId",
          fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
          selectValue: "value",
          shortWidth: true,
          options: LenderLoanTypeList,
          onChange: ({ value }) => {
            setFieldValue("loanTypeId", value);
          }
        },
        {
          label: "Rate Type",
          fieldName: "lpsRateType",
          fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
          selectValue: "value",
          shortWidth: true,
          options: interestRateTypeIds,
          onChange: ({ value }) => {
            setFieldValue("lpsRateType", value);
          }
        },
        {
          label: "Rate Index",
          fieldName: "lpsRateIndex",
          fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
          shortWidth: true,
          options: rateIndexOptions,
          selectValue: "value",
          onChange: ({ value }) => {
            setFieldValue("lpsRateIndex", value);
          }
        },
        {
          label: "Spread",
          fieldName: "spread",
          fieldType: fieldTypes.PERCENT_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("spread", target.value);
          },
          decimalScale: 2
        },
        {
          label: "Amortization Term (m)",
          fieldName: "lpsAmortizationTerm",
          fieldType: fieldTypes.INTEGER_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("lpsAmortizationTerm", target.value);
          }
        },
        {
          label: "Underwriting Rate (Float)",
          fieldName: "uwRate",
          fieldType: fieldTypes.PERCENT_INPUT,
          selectValue: "value",
          hidden: amortRateFieldHidden,
          onChange: ({ target }) => {
            setFieldValue("uwRate", target.value);
          },
          decimalScale: 2
        },
        {
          label: "NOI",
          fieldName: "noi",
          fieldType: fieldTypes.MONEY_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("noi", target.value);
          },
          decimalScale: 2
        },
        {
          label: "Cap Rate",
          fieldName: "futureCapRate",
          fieldType: fieldTypes.PERCENT_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("futureCapRate", target.value);
          },
          decimalScale: 2
        },
        {
          label: "Min DSCR",
          fieldName: "dscrMin",
          fieldType: fieldTypes.DECIMAL_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("dscrMin", target.value);
          },
          decimalScale: 2
        },
        {
          label: "Max LTV",
          fieldName: "ltvMax",
          fieldType: fieldTypes.PERCENT_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("ltvMax", target.value);
          },
          decimalScale: 2
        },
        {
          label: "Min Debt Yield",
          fieldName: "debtYieldMin",
          fieldType: fieldTypes.PERCENT_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("debtYieldMin", target.value);
          },
          decimalScale: 2
        },
        {
          label: "Preferred UW Constraint",
          fieldName: "lpsConstraintPreferenceId",
          fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
          selectValue: "value",
          shortWidth: true,
          options: preferredConstraintsList,
          onChange: ({ value }) => {
            setFieldValue("lpsConstraintPreferenceId", value);
          }
        }
      ]
    }
  ];

  const cashflowAssumptionsSection = [
    {
      sectionTitle: "Cashflow Assumptions",
      fields: [
        {
          label: "Start Date",
          fieldName: "startDate",
          fieldType: fieldTypes.DATE_PICKER,
          shortWidth: true,
          selectValue: "value",
          onChange: e => {
            setFieldValue("startDate", e?._d);
          }
        },
        {
          label: "Loan Term (M)",
          fieldName: "loanTerm",
          fieldType: fieldTypes.INTEGER_INPUT,
          selectValue: "value",
          shortWidth: true,
          onChange: ({ target }) => {
            setFieldValue("loanTerm", target.value);
          }
        },
        {
          label: "Day Count",
          fieldName: "lpsDaycount",
          fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
          shortWidth: true,
          options: daycountIdsList,
          selectValue: "value",
          onChange: ({ value }) => {
            setFieldValue("lpsDaycount", value);
          }
        },
        {
          label: "Closing Costs (%)",
          fieldName: "lpsClosingCosts",
          fieldType: fieldTypes.PERCENT_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("lpsClosingCosts", target.value);
          },
          decimalScale: 2
        },
        {
          label: "Amortization Type",
          fieldName: "lpsAmortizationType",
          fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
          selectValue: "value",
          shortWidth: true,
          options: amortTypesIdsShort,
          onChange: ({ value }) => {
            setFieldValue("lpsAmortizationType", value);
          }
        },
        {
          label: "Amortization Term (m)",
          fieldName: "lpsAmortizationTermForCashflows",
          fieldType: fieldTypes.INTEGER_INPUT,
          selectValue: "value",
          hidden: ioPeriodFieldHidden,
          onChange: ({ target }) => {
            setFieldValue("lpsAmortizationTermForCashflows", target.value);
          }
        },
        {
          label: "I/O Periods",
          fieldName: "lpsIoPeriods",
          fieldType: fieldTypes.INTEGER_INPUT,
          selectValue: "value",
          hidden: ioPeriodFieldHidden,
          onChange: ({ target }) => {
            setFieldValue("lpsIoPeriods", target.value);
          }
        },
        {
          label: "Amortization Rate",
          fieldName: "lpsAmortizationRateForCashflows",
          fieldType: fieldTypes.PERCENT_INPUT,
          selectValue: "value",
          hidden: amortRateFieldHidden,
          onChange: ({ target }) => {
            setFieldValue("lpsAmortizationRateForCashflows", target.value);
          },
          decimalScale: 2
        }
      ]
    }
  ];

  const sections = [...underwritingAssumptionsSection, ...cashflowAssumptionsSection];

  return (
    <Grid container item xs={12} className="assumptions-card-form-section-wrapper">
      <SectionedFieldBody sections={sections} />
    </Grid>
  );
};
